import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Button, Grid, IconButton, List, ListItem, ListItemText } from "@mui/material";
import { Switch, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import { deepCopyObject } from "../../utils/helpers";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";
import TextInputDialog from "./textInputDialog";

// Template shown pre-filled in the "Add User" paste box so the expected shape
// is obvious. `uiLabel` is display-only (drop-down label in the test harness).
const NEW_USER_PROFILE_TEMPLATE = JSON.stringify(
  { uiLabel: "", displayName: "", languageCode: "en-US", origin: "harness" },
  null,
  2,
);

const BotpressBotDialog = props => {
  const {
    open,
    dialogTitle = "Botpress Definition",
    dialogMessage = null,
    okMessage = "Ok",
    cancelMessage = "Cancel",
    deleteMessage = "delete",
    maxWidth = "md",
    fullWidth = true,
    rowCount = 3,
    omniODM, //omni Data Model, a root level object representation, Pass in a COPY, the only way this works
  } = props;

  const theme = useTheme();

  const [editedValues] = useState(deepCopyObject(omniODM));
  const [workspaceId, setWorkspaceId] = useState(omniODM.omniData.workspaceId);
  const [botId, setBotId] = useState(omniODM.omniData.botId);
  const [webchatClientId, setWebchatClientId] = useState(omniODM.omniData.webchatClientId);
  const [accessToken, setAccessToken] = useState(omniODM.omniData.accessToken);
  const [name, setName] = useState(omniODM.omniData.name);
  const [description, setDescription] = useState(omniODM.omniData.description);
  // `userData` is the deprecated single-user field; kept so existing bots are
  // preserved, but no longer surfaced in the UI (replaced by `userProfiles`).
  const [userData] = useState(omniODM.omniData.userData);
  const [userProfiles, setUserProfiles] = useState(
    Array.isArray(omniODM.omniData.userProfiles) ? omniODM.omniData.userProfiles : [],
  );
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserError, setAddUserError] = useState(null);
  const [botData, setBotData] = useState(omniODM.omniData.botData);
  const [isConversationAnalytics, setIsConversationAnalytics] = useState(
    omniODM.omniData.isConversationAnalytics,
  );
  const [isProduction, setIsProduction] = useState(omniODM.omniData.isProduction);

  const [showConfirm, setShowConfirm] = useState(false);

  const onDeleteHandler = () => {
    props.onDelete(omniODM);
  };

  // Internal tool: accept a raw pasted JSON blob and append it as a user
  // profile. We only guard against invalid JSON — no per-field validation.
  const onAddUserProfile = pastedJson => {
    try {
      const parsedProfile = JSON.parse(pastedJson);
      setUserProfiles(previousProfiles => [...previousProfiles, parsedProfile]);
      setAddUserError(null);
      setShowAddUser(false);
    } catch (error) {
      setAddUserError("That isn't valid JSON — double-check the blob and try again.");
    }
  };

  const onRemoveUserProfile = indexToRemove => {
    setUserProfiles(previousProfiles =>
      previousProfiles.filter((profile, index) => index !== indexToRemove),
    );
  };

  const getUserProfileLabel = profile =>
    profile?.uiLabel || profile?.displayName || profile?.origin || "User";

  const getUserProfileSecondary = profile => {
    const parts = [profile?.displayName, profile?.languageCode, profile?.origin].filter(Boolean);
    return parts.join(" · ");
  };

  const onAcceptHandler = () => {
    editedValues.omniData.workspaceId = workspaceId;
    editedValues.omniData.botId = botId;
    editedValues.omniData.webchatClientId = webchatClientId;
    editedValues.omniData.accessToken = accessToken;
    editedValues.omniData.name = name;
    editedValues.omniData.description = description;
    editedValues.omniData.userData = userData; // deprecated, preserved as-is
    editedValues.omniData.userProfiles = userProfiles;
    editedValues.omniData.botData = botData;
    editedValues.omniData.isConversationAnalytics = isConversationAnalytics;
    editedValues.omniData.isProduction = isProduction;

    if (editedValues._id == null) {
      editedValues._id = uuid();
    }
    props.onAccept(editedValues);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid container item spacing={1}>
          <Grid item size={{ xs: 6 }}>
            {dialogMessage && <DialogMessage>{dialogMessage}</DialogMessage>}
            <OmniTextField
              label="Test Candidate Name"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={name}
              onChange={event => {
                setName(event.target.value);
              }}
              autoFocus
              placeholder={"Bot Alias"}
              required
            />

            <OmniTextField
              label="Description"
              type="textInput"
              value={description}
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              onChange={event => setDescription(event.target.value)}
              placeholder={"Bot Description"}
              multiline
              rows={rowCount}
            />
            <OmniTextField
              label="Workspace ID"
              type="textInput"
              name={uuid()}
              variant="outlined"
              value={workspaceId}
              onChange={event => {
                setWorkspaceId(event.target.value);
              }}
              placeholder={"Copy in the Botpress Workplace ID"}
              required
            />
            <OmniTextField
              label="Bot ID"
              type="textInput"
              name={uuid()}
              variant="outlined"
              value={botId}
              onChange={event => {
                setBotId(event.target.value);
              }}
              placeholder={"The Bot ID is in the URL when editing the bot."}
              required
            />
            <OmniTextField
              label="Webchat Client ID"
              type="textInput"
              name={uuid()}
              variant="outlined"
              value={webchatClientId}
              onChange={event => {
                setWebchatClientId(event.target.value);
              }}
              placeholder={"The webchat client ID from the webchat configuration"}
              required
            />
            <OmniTextField
              label="Access Token"
              type="textInput"
              name={uuid()}
              variant="outlined"
              value={accessToken}
              onChange={event => {
                setAccessToken(event.target.value);
              }}
              placeholder={"The access token for the bot."}
              required
            />
          </Grid>

          <Grid item size={{ xs: 6 }} xs={{ paddingLeft: theme.spacing(1) }}>
            <Grid item size={{ xs: 12 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: theme.palette.text.secondary,
                  marginBottom: "4px",
                }}
              >
                User Profiles
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  minHeight: "132px",
                  maxHeight: "180px",
                  overflowY: "auto",
                }}
              >
                {userProfiles.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, padding: theme.spacing(2) }}
                  >
                    No user profiles yet. Use “Add User” to paste one in.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {userProfiles.map((profile, index) => (
                      <ListItem
                        key={index}
                        divider={index < userProfiles.length - 1}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="remove user profile"
                            onClick={() => onRemoveUserProfile(index)}
                          >
                            <DeleteOutlineIcon fontSize="small" color="secondary" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={getUserProfileLabel(profile)}
                          secondary={getUserProfileSecondary(profile)}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-start", marginTop: "8px" }}>
                <Button
                  onClick={() => {
                    setAddUserError(null);
                    setShowAddUser(true);
                  }}
                  color="secondary"
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                >
                  Add User
                </Button>
              </Box>
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <OmniTextField
                label="Default Bot Data - JSON"
                type="textInput"
                name={uuid()}
                variant="outlined"
                value={JSON.stringify(botData, null, 2)}
                onChange={event => {
                  setBotData(JSON.parse(event.target.value));
                }}
                multiline
                rows={6}
              />
            </Grid>
            <Grid item container size={{ xs: 12 }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "4px",
                  justifyContent: "left",
                }}
              >
                <Switch
                  checked={isConversationAnalytics}
                  onChange={() => {
                    setIsConversationAnalytics(val => {
                      const next = !val;
                      if (next) {
                        setIsProduction(false);
                      }
                      return next;
                    });
                  }}
                />
                <Typography variant="body2" sx={{ marginTop: "8px" }}>
                  Conversation Analysis Agent
                </Typography>
              </Box>
            </Grid>
            <Grid item container size={{ xs: 12 }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "4px",
                  justifyContent: "left",
                }}
              >
                <Switch
                  checked={isProduction}
                  onChange={() => {
                    setIsProduction(val => {
                      const next = !val;
                      if (next) {
                        setIsConversationAnalytics(false);
                      }
                      return next;
                    });
                  }}
                />
                <Typography variant="body2" sx={{ marginTop: "8px" }}>
                  Live In Production
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            paddingLeft: theme.spacing(0.5),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {omniODM._id != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => setShowConfirm(true)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onCloseHandler}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button
            onClick={onAcceptHandler}
            color="secondary"
            disabled={!workspaceId || !botId || !webchatClientId || !name}
            variant="contained"
          >
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this note?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}

      {showAddUser && (
        <TextInputDialog
          open={showAddUser}
          dialogTitle="Add User"
          dialogMessage={addUserError || "Paste in a user profile JSON blob, then press Ok."}
          inputLabel="User Profile JSON"
          inputPlaceholder="Paste user profile JSON here"
          defaultValue={NEW_USER_PROFILE_TEMPLATE}
          multiline
          rows={10}
          maxWidth="sm"
          onAccept={onAddUserProfile}
          onClose={() => {
            setShowAddUser(false);
            setAddUserError(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressBotDialog;
